/*

? One row of the chat list (rebuilt 18 Oct 2026 to the app's row, bbm-android ChatScreen ChatRow).

  - The other side's picture and name, and a chat request's "Request" chip. No profile-type chip
    (Agent, Developer, ...) on the row (removed 19 Oct 2026, as on the app).
  - The preview line: the server's preview (lib/chat/messagePreview.ts) led by its glyph (a photo,
    voice note, document, listing, broadcast, enquiry, location, contact), a deleted message in
    italics ("You deleted this message" on the side that sent it), "Unavailable" for a business that
    is no longer live, and "typing…" while the other side types.
  - The time (the app's stamp, lib/chatDates.ts) and the unread count, capped at "99+".
  - A search term is highlighted in the name.
  A broadcast from someone the viewer never chatted with (a row from `broadcast-recipients`, no chat
  yet) reads like a chat with its sender and opens as its thread: the card, Accept / Ignore, and the
  composer, whose first message opens the chat (18 Oct 2026).

*/

/* Common Imports */

import { styled, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { setCurrentChatThunk } from "../../../../redux-magic/store";
import { ProfilePicture } from "../../../../redux-magic/sub-interfaces/business-profile-interface";
import {
	LastMessageInterface,
	NotificationSettingInterface,
	Participants,
	UnreadMessagesCountUser,
} from "../../../../redux-magic/sub-interfaces/chat-interface";

/* Component Imports */

import { Typography } from "@mui/material";
import {
	AttachFileOutlined,
	Block,
	CampaignOutlined,
	HomeWorkOutlined,
	ImageOutlined,
	InsertDriveFileOutlined,
	LocationOnOutlined,
	MicNoneOutlined,
	PersonOutline,
	QuestionAnswerOutlined,
} from "@mui/icons-material";
import { formatChatListStamp } from "../../../../lib/chatDates";

/* Styled Components */

const ChatCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.75rem",
	padding: "0rem",
	cursor: "pointer",
}));

const DivColumn = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	minWidth: 0,
	flex: 1,
	gap: "0.25rem",
}));

const HeadContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	minWidth: 0,
	flex: 1,
	gap: "0.75rem",
}));

const CustomImg = styled("img")(({ theme }) => ({
	// The app's avatar: a rounded square, corners at 30% (BbmAvatar).
	borderRadius: "30%",
	boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
	height: "2.75rem",
	width: "2.75rem",
	flexShrink: 0,
	objectFit: "cover",
}));

const NameRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.375rem",
	minWidth: 0,
	width: "100%",
}));

const TitleCard = styled(Typography)(({ theme }) => ({
	fontSize: "0.9375rem",
	fontWeight: 700,
	lineHeight: "1.25rem",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
	minWidth: 0,
}));

const Chip = styled("span")(({ theme }) => ({
	flexShrink: 0,
	padding: "0.0625rem 0.5rem",
	borderRadius: "999px",
	fontSize: "0.6875rem",
	fontWeight: 500,
	lineHeight: "1.125rem",
	whiteSpace: "nowrap",
}));

const PreviewRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: "0.25rem",
	minWidth: 0,
	width: "100%",
}));

const PreviewText = styled(Typography)(({ theme }) => ({
	fontSize: "0.8125rem",
	lineHeight: "1rem",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
	minWidth: 0,
}));

const DayContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-end",
	flexShrink: 0,
	gap: "0.375rem",
}));

const DaysAgo = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	fontWeight: 400,
	lineHeight: "1rem",
	whiteSpace: "nowrap",
}));

const UnreadMessagesCount = styled("span")(({ theme }) => ({
	display: "inline-flex",
	justifyContent: "center",
	alignItems: "center",
	fontWeight: 700,
	fontSize: "0.75rem",
	minWidth: "1.25rem",
	height: "1.25rem",
	padding: "0rem 0.375rem",
	color: "white",
	background: theme.palette.primary.main,
	borderRadius: "999px",
	lineHeight: 1,
}));

/* The preview's glyph per kind (the app's chatPreviewIcon); plain text has none. */
const PREVIEW_ICONS: Record<string, React.ElementType> = {
	photo: ImageOutlined,
	document: InsertDriveFileOutlined,
	voice_note: MicNoneOutlined,
	listing: HomeWorkOutlined,
	broadcast: CampaignOutlined,
	enquiry: QuestionAnswerOutlined,
	attachment: AttachFileOutlined,
	location: LocationOnOutlined,
	contact: PersonOutline,
	deleted: Block,
};

const ChatListCard = ({
	session,
	dispatch,
	router,
	profile_context,
	chat_id,
	chat_type,
	chat_title,
	chat_description,
	chat_dp,
	chat_notification_settings,
	chat_participants,
	chat_last_message,
	chat_created_at,
	chat_is_broadcast,
	chat_unread_messages_count,
	isBroadcast,
	broadcast_id,
	property_id,
	invite_status,
	is_request,
	server_preview,
	preview_kind,
	is_unavailable,
	is_typing,
	highlight,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	chat_id: ObjectId | string;
	chat_type: string;
	chat_title: string;
	chat_description: string;
	chat_dp: ProfilePicture;
	chat_notification_settings: NotificationSettingInterface;
	chat_participants: Array<Participants>;
	chat_last_message: LastMessageInterface;
	chat_created_at: string;
	chat_is_broadcast: boolean;
	chat_unread_messages_count: UnreadMessagesCountUser;
	isBroadcast: boolean;
	broadcast_id: ObjectId | string | undefined;
	property_id: ObjectId | string | undefined;
	invite_status: string | undefined;
	// A chat request the viewer hasn't accepted yet (chat/fetch-user-chats, chat audit 2.2).
	is_request?: boolean;
	// The server's preview line (lib/chat/messagePreview.ts): "Photo", "Voice note", "Listing"...
	server_preview?: string;
	// What the newest message is (the preview's `kind`), for its glyph.
	preview_kind?: string;
	// The other side is a business that is no longer live: the row reads "Unavailable".
	is_unavailable?: boolean;
	// The other side is typing right now (18 Oct 2026).
	is_typing?: boolean;
	// The search term, highlighted in the name.
	highlight?: string;
}) => {
	const theme = useTheme();

	/* Translations */

	const ar: boolean = router.locale === "ar-AE";
	const kn: boolean = router.locale === "kn-IN";
	const localLang = {
		now: ar ? "الآن" : kn ? "ಈಗ" : "Now",
		request: ar ? "طلب" : kn ? "ವಿನಂತಿ" : "Request",
		yesterday: ar ? "أمس" : kn ? "ನಿನ್ನೆ" : "Yesterday",
		unavailable: ar ? "غير متاح" : kn ? "ಲಭ್ಯವಿಲ್ಲ" : "Unavailable",
		typing: ar ? "يكتب…" : kn ? "ಟೈಪ್ ಮಾಡುತ್ತಿದ್ದಾರೆ…" : "typing…",
		unnamed: ar ? "بدون اسم" : kn ? "ಹೆಸರಿಲ್ಲ" : "Unnamed",
		you_deleted: ar ? "لقد حذفت هذه الرسالة" : kn ? "ನೀವು ಈ ಸಂದೇಶವನ್ನು ಅಳಿಸಿದ್ದೀರಿ" : "You deleted this message",
		was_deleted: ar ? "تم حذف هذه الرسالة" : kn ? "ಈ ಸಂದೇಶವನ್ನು ಅಳಿಸಲಾಗಿದೆ" : "This message was deleted",
	};

	/* Chat receiver: the first participant that is not the acting party (chat audit I12). */
	const idOfParty = (value: any): string =>
		value && typeof value === "object" && "_id" in value ? String(value._id || "") : value ? String(value) : "";
	const viewerUserId: string = session?.user?.dbUser?._id ? String(session.user.dbUser._id) : "";
	const isViewerParty = (participant: any): boolean =>
		profile_context?.is_business_profile === true
			? participant?.is_business_profile === true &&
				idOfParty(participant?.business_profile_id) === String(profile_context?.business_profile_id || "")
			: participant?.is_business_profile !== true && idOfParty(participant?.user_id) === viewerUserId;
	const private_chat_receiver: Participants =
		(chat_participants || []).find((participant: any) => !isViewerParty(participant)) || (chat_participants || [])[0];

	/* The list stamp, in the app's style (lib/chatDates.ts). */
	const getFormattedDateTime = (date: Date): string =>
		formatChatListStamp(date, router.locale, { now: localLang.now, yesterday: localLang.yesterday });

	/* Handle Click chat */

	const unread: number = Number(chat_unread_messages_count?.unread_messages_count || 0);

	const handleClickChat = () => {
		dispatch(
			setCurrentChatThunk({
				property_id: property_id,
				chat_id: chat_id,
				chat_type: chat_type,
				chat_title: chat_title,
				chat_description: chat_description,
				chat_dp: chat_dp,
				chat_is_broadcast: chat_is_broadcast,
				chat_notification_settings: chat_notification_settings,
				chat_participants: chat_participants,
				chat_created_at: chat_created_at,
				chat_source_collection: isBroadcast ? "broadcast" : "chat",
				chat_last_message: chat_last_message,
				chat_broadcast_id: isBroadcast ? broadcast_id : undefined,
				invite_status: isBroadcast ? invite_status : undefined,
				has_unread_message: unread > 0,
				is_request: is_request === true,
			}),
		);
	};

	const receiverBusiness: any =
		private_chat_receiver?.is_business_profile === true && typeof private_chat_receiver.business_profile_id === "object"
			? private_chat_receiver.business_profile_id
			: null;
	const receiverUser: any =
		private_chat_receiver?.is_business_profile !== true && typeof private_chat_receiver?.user_id === "object"
			? private_chat_receiver.user_id
			: null;

	// A direct chat names the other side, a broadcast with no chat yet included (the server dresses
	// it as a direct chat with its sender, 18 Oct 2026); a group its title.
	const chat_name: string =
		(chat_type === "direct"
			? receiverBusiness
				? String(receiverBusiness.title || "")
				: receiverUser
					? `${receiverUser.first_name || ""} ${receiverUser.last_name || ""}`.trim()
					: ""
			: chat_title) || localLang.unnamed;

	const chat_dp_url: string = receiverBusiness
		? String(receiverBusiness.display_picture_url?.file_id || "")
		: receiverUser
			? String(receiverUser.profile_picture?.file_id || "")
			: "";

	// The other side's own picture. Only an OLD one-chat-per-broadcast thread carries a ready link
	// (the listing's cover); a broadcast with no chat yet shows its sender's logo like any chat.
	const profilePicture =
		chat_dp_url.includes("/images/icons/Avatar.svg") || chat_dp_url === ""
			? "/images/icons/Avatar.svg"
			: chat_is_broadcast && !isBroadcast
				? chat_dp_url
				: `/profile-picture/${chat_dp_url}`;

	/* The preview line. A deleted newest message reads "You deleted this message" on the side that
	   sent it, decided by party as in the thread. */

	const last_message_is_own: boolean = chat_last_message
		? profile_context?.is_business_profile === true
			? chat_last_message.sender_is_business_profile === true &&
				String(chat_last_message.sender_business_profile_id) === String(profile_context.business_profile_id)
			: chat_last_message.sender_is_business_profile !== true &&
				viewerUserId !== "" &&
				String(chat_last_message.sender_user_id) === viewerUserId
		: false;

	const isDeletedPreview: boolean = preview_kind === "deleted" || chat_last_message?.body === "This message was deleted";

	const previewText: string = isDeletedPreview
		? last_message_is_own
			? localLang.you_deleted
			: localLang.was_deleted
		: server_preview && server_preview.trim().length > 0
			? server_preview
			: chat_last_message?.body || "";

	const PreviewIcon: React.ElementType | undefined =
		is_unavailable || is_typing ? undefined : PREVIEW_ICONS[isDeletedPreview ? "deleted" : String(preview_kind || "")];

	/* The search term in the name, on a cream mark (the app's chat highlight). */
	const highlightedName = (): React.ReactNode => {
		const term: string = (highlight || "").trim();
		if (!term) return chat_name;
		const at: number = chat_name.toLowerCase().indexOf(term.toLowerCase());
		if (at < 0) return chat_name;
		return (
			<React.Fragment>
				{chat_name.slice(0, at)}
				<span style={{ background: "rgba(252, 128, 25, 0.18)", color: "#B05911", borderRadius: "0.25rem" }}>
					{chat_name.slice(at, at + term.length)}
				</span>
				{chat_name.slice(at + term.length)}
			</React.Fragment>
		);
	};

	return (
		<ChatCard onClick={handleClickChat}>
			<HeadContainer>
				{/* Profile Picture */}

				<CustomImg
					src={profilePicture as string}
					alt={chat_name}
				/>

				<DivColumn>
					<NameRow>
						<TitleCard variant="body1">{highlightedName()}</TitleCard>
						{/* A chat request waiting for the viewer's answer, as the app marks it. */}
						{is_request ? (
							<Chip sx={{ background: "rgba(245, 158, 11, 0.14)", color: "#B45309" }}>{localLang.request}</Chip>
						) : null}
					</NameRow>
					<PreviewRow>
						{PreviewIcon ? <PreviewIcon sx={{ fontSize: "0.9375rem", color: "#A2917C", flexShrink: 0 }} /> : null}
						<PreviewText
							variant="body2"
							sx={{
								fontStyle: !is_unavailable && !is_typing && isDeletedPreview ? "italic" : "normal",
								color: is_typing
									? theme.palette.primary.main
									: is_unavailable
										? theme.palette.text.disabled
										: theme.palette.text.secondary,
							}}
						>
							{is_unavailable ? localLang.unavailable : is_typing ? localLang.typing : previewText}
						</PreviewText>
					</PreviewRow>
				</DivColumn>
			</HeadContainer>

			<DayContainer>
				<DaysAgo sx={{ color: unread > 0 ? theme.palette.primary.main : theme.palette.text.secondary }}>
					{/* A pending request may carry no message time; its own date stands in (chat audit I7). */}
					{getFormattedDateTime(new Date((chat_last_message?.sent_at as any) || chat_created_at))}
				</DaysAgo>
				{unread > 0 ? <UnreadMessagesCount>{unread > 99 ? "99+" : unread}</UnreadMessagesCount> : null}
			</DayContainer>
		</ChatCard>
	);
};

export default ChatListCard;
